import { useState } from "react";

import { PlusIcon } from "@phosphor-icons/react";

import { Banner, Button, Text } from "@alpacahq/ui/old";

import { FloatingButtons } from "../../../../components/common/FloatingButtons";
import type { IRAOnboardingProps } from "..";
import { BeneficiaryModal, type FormBeneficiary } from "./BeneficiaryModal";
import { BeneficiaryTable, type LocalBeneficiary } from "./BeneficiaryTable";
import { CommunityPropertyBanner } from "./CommunityPropertyBanner";

const MAX_BENEFICIARIES = 6;
// All beneficiaries have these fields/values
const BENEFICIARY_CONSTANTS = {
  middle_name: null,
} as const;

export const AddBeneficiaries = ({
  tradeAccountDetails,
  beneficiaries: initialBeneficiaries,
  onSaveSessionState,
}: IRAOnboardingProps) => {
  const [beneficiaries, setBeneficiaries] = useState<LocalBeneficiary[]>(() =>
    initialBeneficiaries.map((b) => ({ ...b, localId: crypto.randomUUID() })),
  );

  const [beneficiaryModal, setBeneficiaryModal] = useState<{
    isOpen: boolean;
    editBeneficiary: LocalBeneficiary | null;
    type: "primary" | "contingent";
  }>({
    isOpen: false,
    editBeneficiary: null,
    type: "primary",
  });

  const onAddBeneficiaryClicked = (type: "primary" | "contingent") => () =>
    setBeneficiaryModal({
      isOpen: true,
      editBeneficiary: null,
      type,
    });

  const onDeleteBeneficiaryClicked = (localId: string) =>
    setBeneficiaries((prev) => prev.filter((b) => b.localId !== localId));

  const onEditBeneficiaryClicked = (localId: string) => {
    const beneficiary = beneficiaries.find((b) => b.localId === localId);
    if (!beneficiary) {
      return;
    }
    setBeneficiaryModal({
      isOpen: true,
      editBeneficiary: beneficiary,
      type: beneficiary.type,
    });
  };

  const onAllocationChange = (localId: string, sharePct: number) =>
    setBeneficiaries((prev) =>
      prev.map((b) =>
        b.localId === localId ? { ...b, share_pct: sharePct } : b,
      ),
    );

  const onCloseModal = () => {
    setBeneficiaryModal({
      isOpen: false,
      editBeneficiary: null,
      type: "primary",
    });
  };

  const onSaveBeneficiary = (beneficiary: FormBeneficiary) => {
    const editLocalId = beneficiaryModal.editBeneficiary?.localId;
    setBeneficiaries((prev) => {
      if (editLocalId) {
        return prev.map((b) =>
          b.localId === editLocalId
            ? {
                ...b,
                ...beneficiary,
                ...BENEFICIARY_CONSTANTS,
              }
            : b,
        );
      }
      return [
        ...prev,
        {
          ...beneficiary,
          localId: crypto.randomUUID(),
          type: beneficiaryModal.type,
          share_pct: 0,
          ...BENEFICIARY_CONSTANTS,
        },
      ];
    });
    setBeneficiaryModal({
      isOpen: false,
      editBeneficiary: null,
      type: "primary",
    });
  };

  const primaryBeneficiaries = beneficiaries.filter(
    (b) => b.type === "primary",
  );
  const contingentBeneficiaries = beneficiaries.filter(
    (b) => b.type === "contingent",
  );

  const totalAllocation = beneficiaries.reduce(
    (acc, b) => acc + (b.share_pct ?? 0),
    0,
  );

  const isAddBeneficiaryButtonDisabled =
    beneficiaries.length >= MAX_BENEFICIARIES;

  const isNextButtonDisabled = !(
    beneficiaries.length <= MAX_BENEFICIARIES &&
    (beneficiaries.length === 0 ||
      (beneficiaries.every((b) => b.share_pct >= 1) && totalAllocation === 100))
  );

  return (
    <div className="flex h-full w-full flex-col justify-start gap-4">
      <CommunityPropertyBanner tradeAccountDetails={tradeAccountDetails} />

      <Banner
        variant={
          totalAllocation === 100
            ? "success"
            : totalAllocation > 100
              ? "error"
              : "info"
        }
        title={`Current Allocation = ${totalAllocation}%`}
        message={
          totalAllocation === 0 || totalAllocation === 100
            ? "The total allocation must be equal 100% across all beneficiaries."
            : "The total allocation must be equal 100% across all beneficiaries. Please add or adjust allocations."
        }
      />

      <div className="flex flex-col gap-8">
        <section className="w-full">
          <h2 className="m-0 p-0 text-left font-semibold text-base text-gray-800">
            Primary Beneficiaries
          </h2>
          <div className="mt-4 flex flex-col gap-4">
            <Text className="text-gray-700 text-sm">
              Primary beneficiaries have the first priority to receive the
              assets from the account.
            </Text>
            {primaryBeneficiaries.length > 0 && (
              <Button
                variant="v2/filled"
                onClick={onAddBeneficiaryClicked("primary")}
                className="w-fit"
                disabled={isAddBeneficiaryButtonDisabled}
              >
                {isAddBeneficiaryButtonDisabled ? (
                  "Max beneficiaries reached"
                ) : (
                  <>
                    {" "}
                    <PlusIcon size={20} />
                    Add Beneficiary
                  </>
                )}
              </Button>
            )}
            <BeneficiaryTable
              beneficiaries={primaryBeneficiaries}
              totalAllocation={totalAllocation}
              onAllocationChange={onAllocationChange}
              onDelete={onDeleteBeneficiaryClicked}
              onEdit={onEditBeneficiaryClicked}
              emptyMessage={
                <div className="flex flex-col items-center justify-center gap-4 py-12">
                  <Text className="text-base text-gray-400">
                    Add primary beneficiaries.
                  </Text>
                  <Button
                    variant="v2/filled"
                    onClick={onAddBeneficiaryClicked("primary")}
                    disabled={isAddBeneficiaryButtonDisabled}
                  >
                    {isAddBeneficiaryButtonDisabled ? (
                      "Max beneficiaries reached"
                    ) : (
                      <>
                        <PlusIcon size={20} />
                        Add Beneficiary
                      </>
                    )}
                  </Button>
                </div>
              }
            />
          </div>
        </section>

        <section className="w-full">
          <h2 className="m-0 p-0 text-left font-semibold text-base text-gray-800">
            Contingent Beneficiaries
          </h2>
          <div className="mt-4 flex flex-col gap-4">
            <Text className="text-gray-700 text-sm">
              Contingent beneficiaries are eligible to inherit assets if there
              are no surviving primary beneficiaries.
            </Text>
            {contingentBeneficiaries.length > 0 && (
              <Button
                variant="v2/filled"
                onClick={onAddBeneficiaryClicked("contingent")}
                className="w-fit"
                disabled={isAddBeneficiaryButtonDisabled}
              >
                {isAddBeneficiaryButtonDisabled ? (
                  "Max beneficiaries reached"
                ) : (
                  <>
                    <PlusIcon size={20} />
                    Add Beneficiary
                  </>
                )}
              </Button>
            )}
            <BeneficiaryTable
              beneficiaries={contingentBeneficiaries}
              totalAllocation={totalAllocation}
              onAllocationChange={onAllocationChange}
              onDelete={onDeleteBeneficiaryClicked}
              onEdit={onEditBeneficiaryClicked}
              emptyMessage={
                <div className="flex flex-col items-center justify-center gap-4 py-12">
                  <Text className="text-base text-gray-400">
                    Add contingent beneficiaries.
                  </Text>
                  <Button
                    variant="v2/filled"
                    onClick={onAddBeneficiaryClicked("contingent")}
                    disabled={isAddBeneficiaryButtonDisabled}
                  >
                    {isAddBeneficiaryButtonDisabled ? (
                      "Max beneficiaries reached"
                    ) : (
                      <>
                        <PlusIcon size={20} />
                        Add Beneficiary
                      </>
                    )}
                  </Button>
                </div>
              }
            />
          </div>
        </section>
      </div>

      {beneficiaryModal.isOpen && (
        <BeneficiaryModal
          onClose={onCloseModal}
          onSave={onSaveBeneficiary}
          editBeneficiary={beneficiaryModal.editBeneficiary}
        />
      )}

      <FloatingButtons
        fixedOnBottom
        className="bg-white"
        next={{
          disabled: isNextButtonDisabled,
          onClick: () => {
            onSaveSessionState({
              step: "stock_lending",
              beneficiaries: beneficiaries.map(({ localId, ...rest }) => rest),
            });
          },
          label: beneficiaries.length === 0 ? "Skip" : "Continue",
        }}
        back={{
          onClick: () => {
            onSaveSessionState({
              step: "information",
              beneficiaries: beneficiaries.map(({ localId, ...rest }) => rest),
            });
          },
        }}
      />
    </div>
  );
};
